import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NavMenu } from '@shopify/app-bridge-react';

import { isPreview } from '@/providers/session';

/**
 * The admin's left-hand navigation.
 *
 * Rendered by Shopify OUTSIDE our iframe, like the save bar: `<NavMenu>` emits
 * `<ui-nav-menu>`, App Bridge reads the anchors inside it once, and mirrors them
 * into the admin's own sidebar. Our copy is never the thing the merchant clicks.
 *
 * ── ⚠ These MUST be React Router `<Link>`s, and they used to be raw `<a>` ──
 *
 * The mirrored item is not a link of App Bridge's own: clicking it dispatches a
 * click on OUR anchor, the one still sitting in the iframe. A bare
 * `<a href="/schedule">` handles that click the way the browser handles any
 * anchor — a full document navigation. The whole SPA tore down and rebooted on
 * every nav click: `/verify` again, the config fetched again, Polaris and the
 * language chunk parsed again, and a white flash in between. Nothing about it
 * looked broken, which is why it survived — it just made the app feel like a
 * website from 2009.
 *
 * A `<Link>` renders the same `<a href>` — so App Bridge still reads the href it
 * mirrors, and the item is still a real link for middle-click, "open in new tab"
 * and the status bar — but its `onClick` calls `preventDefault()` and pushes the
 * history entry instead. Same markup to App Bridge, client-side navigation to
 * us. This is the shape that ships in xbar and xshield.
 *
 * The router context reaches here perfectly well: `<ui-nav-menu>` is an ordinary
 * custom element in OUR React tree, and it is App Bridge that re-parents the
 * rendered DOM afterwards. React contexts follow the React tree, not the DOM.
 *
 * ⚠ The FIRST child must be the home link and must resolve to `/`. App Bridge
 * treats it as the app's root and does not render it as a menu item — it is how
 * the app title itself becomes clickable. Put a real destination first and it
 * silently disappears from the menu.
 *
 * Built for Shopify §4.1.2 requires an app with more than one page to expose
 * this; without it the merchant's only way between pages is whatever links the
 * page happens to contain.
 */
export function AppNavMenu() {
  const { t } = useTranslation();
  // In the preview harness there is no App Bridge, so `<ui-nav-menu>` is an
  // undefined custom element and the browser renders its children INLINE at the
  // top of the page — a row of raw links that looks like a broken layout and
  // would skew the smoke harness's overflow checks.
  if (isPreview()) return null;

  return (
    <NavMenu>
      {/* Not a menu item — this is the root marker. See above. */}
      <Link to="/" rel="home">
        Confetto{/* i18n-exempt — the brand. It is the same word in all 20 locales. */}
      </Link>
      {/*
        Three items, and the six surface pages are one click from Home.
        Built for Shopify §4.1.2 asks for navigation, not for every page in it —
        and a menu with ten entries is how a merchant stops reading the menu.
      */}
      <Link to="/schedule">{t('nav.schedule')}</Link>
      <Link to="/settings">{t('nav.settings')}</Link>
      <Link to="/plans">{t('nav.plans')}</Link>
    </NavMenu>
  );
}
